class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });

    this.ensureResponsiveNavigationStyles();
    this.onNavResponsiveResize = this.handleResponsiveNavigationResize.bind(this);
    this.scheduleResponsiveNavigation();
    window.addEventListener("resize", this.onNavResponsiveResize);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) {
          this.scheduleResponsiveNavigation();
        }
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onNavResponsiveResize);

    if (this.navResponsiveFrame) {
      cancelAnimationFrame(this.navResponsiveFrame);
    }

    if (this.navResponsiveMeasureFrame) {
      cancelAnimationFrame(this.navResponsiveMeasureFrame);
    }

    if (this.navResponsiveResizeTimer) {
      clearTimeout(this.navResponsiveResizeTimer);
    }

    if (this.navResponsiveRepairTimer) {
      clearTimeout(this.navResponsiveRepairTimer);
    }
  }

  ensureResponsiveNavigationStyles() {
    if (document.getElementById("header-responsive-navigation-styles")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "header-responsive-navigation-styles";
    style.textContent = `
.site-header .f-site-nav__list > .f-site-nav__item > .f-site-nav__link {
  white-space: nowrap;
}
.site-header--nav-compact > .container {
  max-width: none;
  padding-left: 24px;
  padding-right: 24px;
}
@media (min-width: 1024px) {
  .site-header--nav-compact.site-header--design-1 .site-header__left,
  .site-header--nav-compact.site-header--design-1 .site-header__right {
    flex: 0 0 150px;
    min-width: 150px;
  }
  .site-header--nav-compact.site-header--design-1 .site-header__center {
    flex: 1 1 auto;
    min-width: 0;
  }
  .site-header--nav-compact .f-site-nav {
    --nav-item-px: 1.2rem;
  }
  .site-header--nav-compact .f-site-nav__list {
    flex-wrap: nowrap;
    justify-content: center;
  }
}
.site-header--nav-collapsed .site-header__menu {
  display: none !important;
}
.site-header--nav-collapsed .mobile-nav-toggle {
  display: flex !important;
}
.f-section-header--nav-collapsed > [class~="xl-down:block"] {
  display: block !important;
}
@media (max-width: 1080px) {
  .site-header .site-header__menu {
    display: none !important;
  }
  .site-header .mobile-nav-toggle {
    display: flex !important;
  }
  .f-section-header > [class~="xl-down:block"] {
    display: block !important;
  }
}`;
    document.head.appendChild(style);
  }

  scheduleResponsiveNavigation() {
    if (this.navResponsiveFrame) {
      cancelAnimationFrame(this.navResponsiveFrame);
    }

    this.navResponsiveFrame = requestAnimationFrame(() => {
      this.navResponsiveFrame = null;
      this.updateResponsiveNavigation();
      this.scheduleResponsiveNavigationRepair();
    });
  }

  handleResponsiveNavigationResize() {
    this.scheduleResponsiveNavigation();
    this.scheduleResponsiveNavigationRepair();

    if (this.navResponsiveResizeTimer) {
      clearTimeout(this.navResponsiveResizeTimer);
    }

    this.navResponsiveResizeTimer = setTimeout(() => {
      this.scheduleResponsiveNavigation();
      this.scheduleResponsiveNavigationRepair();
    }, 150);
  }

  scheduleResponsiveNavigationRepair() {
    if (this.navResponsiveRepairTimer) {
      clearTimeout(this.navResponsiveRepairTimer);
    }

    this.navResponsiveRepairTimer = setTimeout(() => {
      this.navResponsiveRepairTimer = null;
      this.repairResponsiveNavigation();
    }, 250);
  }

  repairResponsiveNavigation() {
    const headerSection = this.headerSection;

    if (!headerSection) {
      return;
    }

    if (window.innerWidth <= 1080) {
      this.classList.remove("site-header--nav-compact");
      this.collapseResponsiveNavigation(headerSection);
      return;
    }

    if (this.classList.contains("site-header--nav-collapsed")) {
      return;
    }

    if (!this.isDesktopNavigationWrapped()) {
      return;
    }

    if (!this.classList.contains("site-header--nav-compact")) {
      this.classList.add("site-header--nav-compact");
    }

    this.navResponsiveMeasureFrame = requestAnimationFrame(() => {
      this.navResponsiveMeasureFrame = null;

      if (this.isDesktopNavigationWrapped()) {
        this.classList.remove("site-header--nav-compact");
        this.collapseResponsiveNavigation(headerSection);
      }
    });
  }

  updateResponsiveNavigation() {
    const headerSection = this.headerSection;

    if (!headerSection) {
      return;
    }

    if (this.navResponsiveMeasureFrame) {
      cancelAnimationFrame(this.navResponsiveMeasureFrame);
    }

    this.classList.remove(
      "site-header--nav-compact",
      "site-header--nav-collapsed"
    );
    headerSection.classList.remove("f-section-header--nav-collapsed");

    if (window.innerWidth <= 1080) {
      this.collapseResponsiveNavigation(headerSection);
      return;
    }

    this.navResponsiveMeasureFrame = requestAnimationFrame(() => {
      this.navResponsiveMeasureFrame = null;

      if (!this.isDesktopNavigationWrapped()) {
        return;
      }

      this.classList.add("site-header--nav-compact");

      this.navResponsiveMeasureFrame = requestAnimationFrame(() => {
        this.navResponsiveMeasureFrame = null;

        if (this.isDesktopNavigationWrapped()) {
          this.classList.remove("site-header--nav-compact");
          this.collapseResponsiveNavigation(headerSection);
        }
      });
    });
  }

  collapseResponsiveNavigation(headerSection) {
    this.classList.add("site-header--nav-collapsed");
    headerSection.classList.add("f-section-header--nav-collapsed");
  }

  isDesktopNavigationWrapped() {
    const navItems = Array.from(
      this.querySelectorAll(".f-site-nav__list > li")
    ).filter((item) => {
      const rect = item.getBoundingClientRect();

      return (
        window.getComputedStyle(item).display !== "none" && rect.width > 0
      );
    });

    if (!navItems.length) {
      return false;
    }

    const rowTops = navItems.map((item) =>
      Math.round(item.getBoundingClientRect().top)
    );

    return new Set(rowTops).size > 1;
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem, index) => {
          megaItem.addEventListener("mouseenter", (evt) =>
            this.onMenuItemEnter(evt, index)
          );
          megaItem.addEventListener("mouseleave", (evt) =>
            this.onMenuItemLeave(evt, index)
          );
        });
    }
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;

    /**
     * If hover from prev item to sibling items then need to close prev sub menu immediately.
     */
    if (!target.classList.contains(this.classes.itemActive)) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.classList.remove(this.classes.itemActive);
        });
    }

    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      });
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);
    
    this.header.handleMegaItemDeactive();

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
    });
  }

  closeMegaDropdowns() {
    clearTimeout(this.timeoutEnter);
    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.classList.remove(this.classes.itemActive);
      });
    this.timeoutLeave = setTimeout(() => {
      this.header.handleMegaItemDeactive();
    });
  }

  disconnectedCallback() {
    this.megaItems &&
      this.megaItems.forEach((megaItem, index) => {
        megaItem.removeEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        megaItem.removeEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
      });
  }
}
customElements.define("site-nav", SiteNav);

class HeaderAccount extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    await customElements.whenDefined('shopify-account');

    this.injectAnimation(account);
    // this.watchDialog(account);
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow || shadow.querySelector('#custom-account-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      @media (min-width: 751px) {
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
      }
    `;

    shadow.prepend(style);
  }

  watchDialog(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    const dialog = shadow.querySelector('dialog');
    if (!dialog) return;

    const update = () => {
      document.documentElement.classList.toggle('account-dialog-open', dialog.hasAttribute('open'));
    };

    update();

    new MutationObserver(update).observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }
}

customElements.define('header-account', HeaderAccount);
